import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutContent, faqItems } from '@/mocks/kunlun-data';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于昆仑雪',
  description: '昆仑雪品牌介绍、荣誉资质与联系方式。昆仑雪是中国高端矿泉水品牌，源自昆仑山万年冰川雪水。',
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    setSubmitting(true);
    setFormError('');

    try {
      const formData = new FormData(form);
      formData.delete('phone_alt');
      const response = await fetch('https://readdy.ai/api/form/d9rgmuee3oq4jqumu15g', {
        method: 'POST',
        body: new URLSearchParams(formData as unknown as Record<string, string>),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const code = parsed?.code;
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        const msg = serverMsg && typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试';
        setFormError(msg);
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="关于我们"
        description="了解昆仑雪品牌故事、荣誉资质与联系方式。昆仑雪是中国高端矿泉水品牌，源自昆仑山万年冰川雪水，坚持极致纯净的品牌理念。"
        keywords="昆仑雪品牌介绍,昆仑雪荣誉,昆仑雪联系方式,高端矿泉水品牌"
        canonicalUrl="https://kunlunsnow.com/about"
      />
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <main>
        <section className="pt-24 pb-10 md:pt-28 md:pb-14 px-4 md:px-6 lg:px-10 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mt-5 mb-2">
              关于我们
            </h1>
            <p className="text-foreground-500 text-sm md:text-base">
              了解昆仑雪的品牌故事、发展历程与核心价值观
            </p>
          </div>
        </section>

        <section id="brand" className="pb-10 md:pb-16 px-4 md:px-6 lg:px-10 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-start">
              <div className="lg:w-1/2">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20serene%20landscape%20of%20the%20Kunlun%20mountain%20range%20with%20pristine%20snow-capped%20peaks%20reflecting%20in%20a%20crystal%20clear%20glacial%20lake%20at%20sunrise%2C%20ethereal%20golden%20light%2C%20pure%20natural%20beauty%2C%20cinematic%20wide-angle%20composition&width=800&height=550&seq=about-brand-kunlun&orientation=landscape"
                  alt="昆仑山壮丽自然景观"
                  title="昆仑雪品牌发源地 - 昆仑山脉"
                  className="w-full h-auto rounded-2xl object-cover"
                />
              </div>
              <div className="lg:w-1/2">
                <span className="text-xs font-medium tracking-[0.2em] text-primary-600 uppercase font-heading">
                  ABOUT US
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-3 mb-5">
                  {aboutContent.brandIntro.title}
                </h2>
                {aboutContent.brandIntro.content.split('\n\n').filter(Boolean).map((paragraph, index) => (
                  <p key={index} className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                    {paragraph.trim()}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="honors" className="py-14 md:py-20 px-4 md:px-6 lg:px-10 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-xs font-medium tracking-[0.2em] text-accent-600 uppercase font-heading">
                HONORS
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mt-3 mb-4">
                荣誉资质
              </h2>
              <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto">
                十余年来，昆仑雪凭借卓越的产品品质获得国内外多项权威大奖与认证
              </p>
            </div>

            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300/60 -translate-x-1/2 hidden md:block"></div>
              <div className="flex flex-col gap-6">
                {aboutContent.honors.map((honor, index) => (
                  <div
                    key={index}
                    className={`flex flex-col md:flex-row gap-4 md:gap-8 items-start ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                      <div className="bg-background-50 rounded-2xl p-5 md:p-6">
                        <span className="text-xs font-bold text-accent-600 font-heading">{honor.year}</span>
                        <h3 className="font-heading text-base font-bold text-foreground-950 mt-1 mb-2">
                          {honor.title}
                        </h3>
                        <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">
                          {honor.description}
                        </p>
                      </div>
                    </div>
                    <div className="hidden md:flex w-8 items-center justify-center flex-shrink-0">
                      <div className="w-3 h-3 rounded-full bg-accent-500 border-2 border-background-100"></div>
                    </div>
                    <div className="flex-1 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className="py-14 md:py-20 px-4 md:px-6 lg:px-10 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-xs font-medium tracking-[0.2em] text-primary-600 uppercase font-heading">
                CONTACT
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mt-3 mb-4">
                联系我们
              </h2>
              <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto">
                如您有任何疑问或合作意向，欢迎通过以下方式联系我们
              </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-10 lg:gap-14">
              <div className="lg:w-2/5">
                <div className="bg-background-100 rounded-2xl p-6 md:p-8">
                  <h3 className="font-heading text-lg font-bold text-foreground-950 mb-6">联系方式</h3>
                  <div className="flex flex-col gap-5">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-map-pin-line text-primary-600"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-400 mb-0.5">地址</p>
                        <p className="text-sm text-foreground-700 font-medium">{aboutContent.contact.address}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-phone-line text-primary-600"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-400 mb-0.5">电话</p>
                        <a href={`tel:${aboutContent.contact.phone}`} className="text-sm text-foreground-700 font-medium hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                          {aboutContent.contact.phone}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-mail-line text-primary-600"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-400 mb-0.5">邮箱</p>
                        <a href={`mailto:${aboutContent.contact.email}`} className="text-sm text-foreground-700 font-medium hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                          {aboutContent.contact.email}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-time-line text-primary-600"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-400 mb-0.5">工作时间</p>
                        <p className="text-sm text-foreground-700 font-medium">{aboutContent.contact.workingHours}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:w-3/5">
                <div className="bg-background-100 rounded-2xl p-6 md:p-8">
                  <h3 className="font-heading text-lg font-bold text-foreground-950 mb-6">发送消息</h3>

                  {formStatus === 'success' ? (
                    <div className="text-center py-10">
                      <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
                        <i className="ri-check-line text-primary-600 text-2xl"></i>
                      </div>
                      <h4 className="font-heading text-lg font-bold text-foreground-950 mb-2">消息已发送</h4>
                      <p className="text-sm text-foreground-500">感谢您的留言，我们会在1-2个工作日内回复您。</p>
                      <button
                        onClick={() => setFormStatus('idle')}
                        className="mt-6 px-6 py-2.5 bg-background-200 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-300 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        发送新消息
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} data-readdy-form className="flex flex-col gap-4">
                      <div className="flex flex-col sm:flex-row gap-4">
                        <div className="flex-1">
                          <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-600 mb-1.5">
                            姓名 <span className="text-red-400">*</span>
                          </label>
                          <input
                            id="contact-name"
                            name="name"
                            type="text"
                            required
                            className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all"
                            placeholder="请输入您的姓名"
                          />
                        </div>
                        <div className="flex-1">
                          <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-600 mb-1.5">
                            邮箱 <span className="text-red-400">*</span>
                          </label>
                          <input
                            id="contact-email"
                            name="email"
                            type="email"
                            required
                            className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all"
                            placeholder="请输入您的邮箱"
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-600 mb-1.5">
                          主题
                        </label>
                        <input
                          id="contact-subject"
                          name="subject"
                          type="text"
                          className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all"
                          placeholder="请输入消息主题"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-600 mb-1.5">
                          留言内容 <span className="text-red-400">*</span>
                        </label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all resize-none"
                          placeholder="请输入您的留言内容（最多500字）"
                        ></textarea>
                      </div>

                      <div className="honeypot-wrap">
                        <input
                          type="text"
                          name="phone_alt"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                        />
                      </div>

                      {formStatus === 'error' && (
                        <p className="text-xs text-red-500 bg-red-50 px-4 py-2.5 rounded-lg">
                          {formError || '提交失败，请稍后重试'}
                        </p>
                      )}

                      <button
                        type="submit"
                        disabled={submitting}
                        className="px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {submitting ? '发送中...' : '发送消息'}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}